import { legacyAuthHeader } from '../utils/legacyHostCompat';
/**
 * Photo Service
 * Handles facility photo uploads, retrieval, voting, and reporting
 */

import { hostWebApp } from './hostWebApp';
const LEGACY_AUTH_USER_ID_HEADER = legacyAuthHeader('User-Id');
const LEGACY_AUTH_USERNAME_HEADER = legacyAuthHeader('Username');
const LEGACY_AUTH_INIT_DATA_HEADER = legacyAuthHeader('Init-Data');


const API_BASE = '/api/photos';

export interface FacilityPhoto {
  id: string;
  facility_id: string;
  facility_type: string;
  host_user_id: string;
  host_username?: string;
  r2_key: string;
  thumbnail_key?: string;
  original_filename?: string;
  mime_type: string;
  file_size: number;
  width?: number;
  height?: number;
  photo_type: 'standard' | 'panorama_360';
  caption?: string;
  tags: string[];
  photo_latitude?: number;
  photo_longitude?: number;
  upvotes: number;
  downvotes: number;
  score: number;
  is_primary: boolean;
  created_at: string;
  thumbnailUrl: string;
  fullUrl: string;
  userUpvoted: boolean;
  userDownvoted: boolean;
}

export interface PhotoUploadData {
  file: File;
  facilityId: string;
  facilityType?: string;
  photoType?: 'standard' | 'panorama_360';
  caption?: string;
  tags?: string[];
  latitude?: number;
  longitude?: number;
}

export interface VoteResult {
  success: boolean;
  upvotes: number;
  downvotes: number;
  score: number;
  userVote: 'up' | 'down' | null;
}

class PhotoService {
  private getUserHeaders() {
    const user = hostWebApp && typeof hostWebApp.getUser === 'function' ? hostWebApp.getUser() : null;
    return {
      [LEGACY_AUTH_USER_ID_HEADER]: (hostWebApp && typeof hostWebApp.getUserId === 'function' ? hostWebApp.getUserId() : '') || '',
      [LEGACY_AUTH_USERNAME_HEADER]: user?.username || user?.first_name || '',
      [LEGACY_AUTH_INIT_DATA_HEADER]: hostWebApp && typeof hostWebApp.getInitData === 'function' ? hostWebApp.getInitData() : ''
    };
  }

  /**
   * Get all photos for a facility
   */
  async getPhotos(facilityId: string, facilityType: string = 'well'): Promise<FacilityPhoto[]> {
    try {
      const response = await fetch(
        `${API_BASE}?facilityId=${encodeURIComponent(facilityId)}&facilityType=${encodeURIComponent(facilityType)}`,
        { headers: this.getUserHeaders() }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch photos');
      }
      
      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error('Error fetching photos:', error);
      return [];
    }
  }

  /**
   * Upload a photo
   */
  async uploadPhoto(data: PhotoUploadData): Promise<{ success: boolean; photoId?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('photo', data.file);
      formData.append('facilityId', data.facilityId);
      formData.append('facilityType', data.facilityType || 'well');
      formData.append('photoType', data.photoType || 'standard');
      
      if (data.caption) {
        formData.append('caption', data.caption);
      }
      
      if (data.tags && data.tags.length > 0) {
        formData.append('tags', JSON.stringify(data.tags));
      }
      
      if (data.latitude) {
        formData.append('latitude', data.latitude.toString());
      }
      
      if (data.longitude) {
        formData.append('longitude', data.longitude.toString());
      }

      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: this.getUserHeaders(),
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Upload failed' };
      }
      
      return { success: true, photoId: result.photoId };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return { success: false, error: 'Failed to upload photo' };
    }
  }

  /**
   * Delete a photo (only owner can delete)
   */
  async deletePhoto(photoId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}?photoId=${encodeURIComponent(photoId)}`, {
        method: 'DELETE',
        headers: this.getUserHeaders()
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error deleting photo:', error);
      return false;
    }
  }

  /**
   * Vote on a photo
   */
  async vote(photoId: string, voteType: 'up' | 'down' | 'none'): Promise<VoteResult | null> {
    try {
      const response = await fetch(`${API_BASE}/vote`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photoId, voteType })
      });

      if (!response.ok) {
        throw new Error('Vote failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error voting:', error);
      return null;
    }
  }

  /**
   * Report a photo
   */
  async report(
    photoId: string, 
    reason: 'inappropriate' | 'inaccurate' | 'duplicate' | 'spam' | 'other',
    description?: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/report`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photoId, reason, description })
      });

      return response.ok;
    } catch (error) {
      console.error('Error reporting photo:', error);
      return false;
    }
  }

  /**
   * Compress image before upload
   */
  async compressImage(
    file: File, 
    maxWidth: number = 1920, 
    quality: number = 0.8
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        let { width, height } = img;
        
        // Scale down if needed
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        } else {
          reject(new Error('Canvas context unavailable'));
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Extract GPS from EXIF data (if available)
   */
  async extractGPS(_file: File): Promise<{ latitude: number; longitude: number } | null> {
    // Basic EXIF parsing - in production, use exif-js library
    // For now, return null and rely on manual location input
    return null;
  }

  /**
   * Get photo tags options
   */
  getTagOptions(): { value: string; label: string; icon: string }[] {
    return [
      { value: 'entrance', label: 'Entrance/Gate', icon: '🚪' },
      { value: 'landmark', label: 'Landmark', icon: '🏔️' },
      { value: 'hazard', label: 'Hazard', icon: '⚠️' },
      { value: 'road-sign', label: 'Road Sign', icon: '🪧' },
      { value: 'tank-battery', label: 'Tank Battery', icon: '🛢️' },
      { value: 'equipment', label: 'Equipment', icon: '⚙️' },
      { value: 'road-condition', label: 'Road Condition', icon: '🛤️' },
      { value: 'night-view', label: 'Night View', icon: '🌙' }
    ];
  }
}

export const photoService = new PhotoService();
